import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { DetailPage } from '@jet-app/music-classical/models/pages/detail';
import type { CropCode } from './types';
import type { Localization } from '../../dependencies/localization';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';

/**
 * @method getInstrumentSeoData
 * @param {string} url Detail canonical URL
 * @param {DetailPage} page Detail page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getInstrumentSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
): SeoData {
    const localization: Localization = inject(
        ObjectGraphType.localization,
        objectGraph,
    );
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const instrument: string = page.header.title;
    const artworkObj = page.header.artwork;
    const crop: CropCode = 'cw';

    function getPageTitle() {
        return localization.string(
            'ASE.Web.MusicClassicalClientJS.Meta.Instrument.Title',
            { instrument },
        );
    }

    function getPageDescription() {
        return localization.string(
            'ASE.Web.MusicClassicalClientJS.Meta.Instrument.Description',
            { instrument },
        );
    }

    const pageTitle: string = getPageTitle();
    const description: string = getPageDescription();

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Instrument Apple-spcific Values
         *
         */
        appleContentId: undefined,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Instrument Artwork Values
         *
         */
        crop,
        twitterCropCode: crop,
        artworkUrl: artworkObj?.template ?? baseSeoData.artworkUrl,
    };
}

export function addingInstrumentSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
): DetailPage {
    return {
        ...page,
        seoData: getInstrumentSeoData(url, page, objectGraph),
    };
}
